import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { policyTypes } from '@ourbranch/policy-types';

import { appConfig } from 'brand-config';
import { ExpandableCard } from 'core/components/expandable-card';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { track } from 'core/helpers/analytics';
import { DiscountRow } from './discount-row';

/**
 * Editable farm discounts for the policy screen (#32069 / #32036 §4.2, restyled in #32231).
 *
 * #32231 makes this section match the new-offer discounts card: an ExpandableCard with an
 * "active of total" badge and per-discount add/remove rows (the offer's DiscountsCard look),
 * instead of the old flat Switch list. It also fixes two reported problems:
 *   - claim-free is underwriter-only — agents can view it but only a UW (canApproveUWReview) can
 *     change it. Every other discount stays editable by any licensed editor.
 *   - toggling a discount now marks the Formik field touched, so the policy "Preview policy
 *     changes" footer actually appears (it gates on `touched`; setFieldValue alone never sets it).
 *
 * Each discount binds to a `segment.global.*` field (the same fields the offer-side farm-discount
 * component writes). `enabledFlag` is the brand `appConfig.discounts` key that gates availability
 * (undefined = always available), mirroring the offer gating in offer/hooks/use-discounts.jsx — a
 * discount the brand doesn't offer is omitted. `entityGated` discounts are only available to an
 * Individual/Joint named insured (not LLC/Trust/etc.), matching the offer side.
 *
 * NOTE: the IMT source carries no discount data, so nothing is auto-populated on migration (only
 * claimFreeDiscount is derived, by the converter) — this section lets staff apply discounts after.
 *
 * #32516: `hideForRental` discounts (Legacy/Military/Education) apply a 1.0 (no-op) ClarionDoor
 * factor on Rental (Y) — they are not applicable to rental, so they are omitted for a Rental policy
 * (matching the offer-side gating in offer/hooks/use-discounts.jsx). They stay on Farm (F)/Home (Z).
 */
const FARM_DISCOUNTS = [
  { field: 'multiPolicyDiscount', label: 'Auto/Home bundle discount', enabledFlag: 'hasAutoHomeBundleDiscount' },
  { field: 'legacyDiscount', label: 'Legacy discount', entityGated: true, hideForRental: true },
  { field: 'militaryDiscount', label: 'Military discount', entityGated: true, hideForRental: true },
  { field: 'educationDiscount', label: 'Education discount', entityGated: true, hideForRental: true },
  { field: 'cropInsuranceDiscount', label: 'Crop insurance discount', enabledFlag: 'hasCropInsuranceDiscount' },
  { field: 'traditionsDiscount', label: 'Traditions discount', enabledFlag: 'hasTraditionsDiscount' },
  { field: 'farmAssociationDiscount', label: 'Association discount', enabledFlag: 'hasAssociationDiscount' },
  // #32231: claim-free is underwriter-only — agents can view but not change it.
  { field: 'claimFreeDiscount', label: 'Claim-free discount', uwOnly: true }
];

const FarmDiscounts = observer(function FarmDiscounts({ disabled }) {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { canApproveUWReview } = useSession();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const brandDiscounts = appConfig?.discounts ?? {};
  const entityType = values?.entity?.type;
  const isIndividualOrJoint = entityType === 'Individual' || entityType === 'Joint';
  const isFarmRental = policyStore?.policy?.policyType === policyTypes.FarmRental;

  const setDiscount = useCallback(
    (field, value) => {
      setFieldValue(`global.${field}`, value);
      // #32231: mark the field touched so the policy "Preview policy changes" footer appears (it
      // gates on Object.keys(touched).length). setFieldValue alone never marks touched, which is
      // why toggling a discount used to leave the preview button hidden. Pass shouldValidate=false
      // to avoid forcing a full-form validation pass on every toggle.
      setFieldTouched(`global.${field}`, true, false);
      track('Farm Discount Toggled', { field, value });
    },
    [setFieldValue, setFieldTouched]
  );

  // Only show discounts the brand offers; mirrors the offer card (it never lists unavailable ones).
  // #32516: also drop rental-inapplicable discounts (Legacy/Military/Education) on a Rental policy.
  const visibleDiscounts = FARM_DISCOUNTS.filter(({ enabledFlag, hideForRental }) => {
    if (hideForRental && isFarmRental) {
      return false;
    }
    return enabledFlag ? Boolean(brandDiscounts[enabledFlag]) : true;
  });
  const activeCount = visibleDiscounts.filter(({ field }) => Boolean(values?.global?.[field])).length;

  return (
    <div id="farm_discounts">
      <ExpandableCard title="Discounts" badge={`${activeCount} of ${visibleDiscounts.length}`}>
        {visibleDiscounts.map(({ field, label, entityGated, uwOnly }) => {
          const isActive = Boolean(values?.global?.[field]);
          const entityEnabled = entityGated ? isIndividualOrJoint : true;
          // claim-free is UW-only (#32231); every other discount is editable by any licensed editor.
          const canChange = !disabled && entityEnabled && (uwOnly ? canApproveUWReview : true);
          return (
            <DiscountRow
              key={field}
              id={field}
              label={label}
              isActive={isActive}
              disabled={disabled}
              onAdd={!isActive && canChange ? () => setDiscount(field, true) : undefined}
              onRemove={isActive && canChange ? () => setDiscount(field, false) : undefined}
            />
          );
        })}
      </ExpandableCard>
    </div>
  );
});

FarmDiscounts.propTypes = {
  disabled: PropTypes.bool
};

FarmDiscounts.defaultProps = {
  disabled: false
};

export default FarmDiscounts;
